import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import i18next from "i18next";
import { ContainerComponent, MobileFloatingFooter, ProgressSteps, SecurityBadges } from "../../ui";
import OrderFlightInfoComponent from "../OrderFlightInfoComponent";
import OrderIdAndStatusComponent from "../OrderIdAndStatusComponent";
import OrderAdditionalOptionsComponent from "../OrderAdditionalOptionsComponent";
import OrderPassengersComponent from "../OrderPassengersComponent";
import OrderPaymentFormComponent from "../OrderPaymentFormComponent";
import OrderPriceComponent from "../OrderPriceComponent";
import OrderRefundRequestComponent from "../OrderRefundRequestComponent";
import OrderSupportComponent from "../OrderSupportComponent";
import { Block, PriceAndButtonsBlock, RoutesDataAndFormBlock, Wrapper } from "./style";
import { checkInAccessToPaymentStatus } from "../../../utils";
import OrderCopyAndCancelComponent from "../OrderCopyAndCancelComponent";

const OrderComponent = ({
  sendPayment,
  cancelOrder,
  reloadOrder,
  notice,
}) => {
  const { orderData, isLoad } = useSelector(state => state.order);
  const { currency } = useSelector(state => state.appConfigs);

  const isAccessToPayment = checkInAccessToPaymentStatus(orderData?.booking_status, orderData?.order_status);

  const [voidStatus, setVoidStatus] = useState(false);
  const [showFloatingFooter, setShowFloatingFooter] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const payButtonRef = useRef(null);

  const handleMobilePayment = () => {
    const payButton = document.getElementById('order-pay-button');
    if (payButton) payButton.click();
  };

  // Hide floating footer when main payment button is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setShowFloatingFooter(!entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    const findAndObserveButton = () => {
      const payButton = document.getElementById('order-pay-button');
      if (payButton) {
        payButtonRef.current = payButton;
        observer.observe(payButton);
      }
    };

    findAndObserveButton();
    const timeout = setTimeout(findAndObserveButton, 500);

    return () => {
      clearTimeout(timeout);
      if (payButtonRef.current) {
        observer.unobserve(payButtonRef.current);
      }
    };
  }, []);

  return (
    <Wrapper>
      <ContainerComponent>
        <ProgressSteps currentStep={3} />

        <Block>
          <RoutesDataAndFormBlock>
            <OrderIdAndStatusComponent />
            <OrderPassengersComponent />
            <OrderFlightInfoComponent />
            {isAccessToPayment && !voidStatus && <OrderAdditionalOptionsComponent reloadOrder={reloadOrder} />}
            <SecurityBadges />
            <OrderCopyAndCancelComponent onCancelOrder={cancelOrder} isShowInSmallScreen />
            <OrderRefundRequestComponent isShowInSmallScreen />
            <OrderSupportComponent isShowInSmallScreen />
          </RoutesDataAndFormBlock>

          <PriceAndButtonsBlock>
            <OrderIdAndStatusComponent isShowInSmallScreen />
            {notice ? (
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#fff7e6", border: "1px solid #ffd591", color: "#ad4e00", fontSize: 14 }}>
                {notice}
              </div>
            ) : null}
            <OrderPriceComponent />
            {isAccessToPayment && !voidStatus && (
              <OrderPaymentFormComponent
                sendPaymentRequest={sendPayment}
                onProcessingChange={setIsPaymentProcessing}
              />
            )}
            <OrderCopyAndCancelComponent onCancelOrder={cancelOrder} />
            <OrderRefundRequestComponent />
            <OrderSupportComponent />
          </PriceAndButtonsBlock>
        </Block>

        {isAccessToPayment && !voidStatus && (
          <MobileFloatingFooter
            totalPrice={orderData?.total_price_with_options ?? orderData?.total_price}
            currency={currency}
            onContinue={handleMobilePayment}
            isLoading={isPaymentProcessing}
            isVisible={showFloatingFooter}
            buttonText={i18next.t("pay_for_the_order")}
          />
        )}
      </ContainerComponent>
    </Wrapper>
  );
};

export default OrderComponent;
